import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './UserSignUp.css';



function UserSignUp() {
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '', zipcode: '', state: '',
  });
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [showPw, setShowPw]         = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false); // set after submit → show check-email screen
  const [resending, setResending]   = useState(false);
  const [resendMsg, setResendMsg]   = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const nextParam = new URLSearchParams(window.location.search).get('next');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!formData.zipcode && !formData.state) {
      setError('Please enter at least a zip code or state.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/register', {
        username: formData.username,
        email:    formData.email,
        password: formData.password,
        zipcode:  formData.zipcode  || undefined,
        state:    formData.state    || undefined,
        next:     nextParam         || undefined,
      });
      if (data.token) {
        setAccessToken(data.token);
        localStorage.setItem('hasSession', 'true');
        localStorage.setItem('userType', data.user.type);
        localStorage.setItem('username', data.user.username);
        navigate(nextParam || '/dashboard');
        return;
      }
      setPendingEmail(formData.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    try {
      await axios.post('/api/auth/resend-verification', { email: pendingEmail });
      setResendMsg('Verification email resent! Check your inbox.');
    } catch (err) {
      setResendMsg(err.response?.data?.error || 'Could not resend. Try again in a moment.');
    } finally {
      setResending(false);
    }
  };

  if (pendingEmail) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', padding: '2rem' }}>
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', padding: '3rem 2.5rem', maxWidth: '440px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '52px', marginBottom: '1rem' }}>📧</div>
          <h2 style={{ color: '#0d3165', fontWeight: 800, marginBottom: '0.5rem' }}>Check your email</h2>
          <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            We sent a verification link to <strong>{pendingEmail}</strong>.<br />
            Click the link to activate your account and start reading.
          </p>
          <p style={{ color: '#888', fontSize: '0.82rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            The link expires in 24 hours. Check your spam folder if you don't see it.
          </p>
          {resendMsg ? (
            <div style={{ fontSize: '13px', color: resendMsg.startsWith('Verification') ? '#27ae60' : '#c0392b', marginBottom: '1rem' }}>{resendMsg}</div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              style={{ background: 'transparent', border: '1px solid #ccc', borderRadius: '6px', padding: '0.6rem 1.5rem', fontSize: '0.85rem', color: '#555', cursor: resending ? 'not-allowed' : 'pointer', opacity: resending ? 0.6 : 1 }}
            >
              {resending ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>

      <div className="signup-container">
        <div className="signup-form-wrapper">
          <img src="/pubsilo.png" alt="PubSilo" className="signup-logo" />
          <h1>Join as Reader</h1>
          <p className="subtitle">Create your account to start reading premium content</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text" id="username" name="username"
                value={formData.username} onChange={handleChange}
                required placeholder="Choose your username"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email" id="email" name="email"
                value={formData.email} onChange={handleChange}
                required placeholder="your@email.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'} id="password" name="password"
                  value={formData.password} onChange={handleChange}
                  required placeholder="Enter password"
                  style={{ paddingRight: '2.5rem', width: '100%' }}
                />
                <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#999', display: 'flex', alignItems: 'center' }}>
                  {showPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPw ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
                  value={formData.confirmPassword} onChange={handleChange}
                  required placeholder="Confirm password"
                  style={{ paddingRight: '2.5rem', width: '100%' }}
                />
                <button type="button" onClick={() => setShowConfirmPw(v => !v)} aria-label={showConfirmPw ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#999', display: 'flex', alignItems: 'center' }}>
                  {showConfirmPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 700, marginBottom: '0.25rem', display: 'block' }}>
                Your Location <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <p style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.6rem', lineHeight: 1.4 }}>
                At least one is required. The more specific, the more relevant content you'll see.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="zipcode" style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.25rem', display: 'block' }}>Zip Code</label>
                  <input
                    type="text" id="zipcode" name="zipcode"
                    value={formData.zipcode} onChange={handleChange}
                    placeholder="e.g. 73301"
                    maxLength={10}
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="state" style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.25rem', display: 'block' }}>State</label>
                  <select
                    id="state" name="state"
                    value={formData.state} onChange={handleChange}
                    style={{ width: '100%' }}
                  >
                    <option value="">— Select —</option>
                    {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <p className="login-prompt">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default UserSignUp;
